/**
 * Содержимое чужого чата + кнопка удаления. Сверху — баннер «admin view».
 */

import { Alert, Badge, Button, Card, Center, Group, Loader, Modal, Paper, Stack, Text, Title } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { deleteAdminChat, fetchAdminChat } from '../../lib/api-admin';
import { DESKTOP_QUERY } from '../../lib/breakpoints';
import { fmtDate } from './format';

export function AdminChatDetailPage(): JSX.Element {
    const params = useParams();
    const sessionId = params.sessionId ?? '';
    const navigate = useNavigate();
    const qc = useQueryClient();
    const isDesktop = useMediaQuery(DESKTOP_QUERY);
    const [confirmOpened, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);

    const q = useQuery({
        queryKey: ['admin-chat', sessionId],
        queryFn:  () => fetchAdminChat(sessionId),
        enabled:  Boolean(sessionId),
        retry:    false,
    });

    const del = useMutation({
        mutationFn: () => deleteAdminChat(sessionId),
        onSuccess:  () => {
            qc.invalidateQueries({ queryKey: ['admin-chats'] });
            navigate('/admin/chats');
        },
    });

    return (
        <Stack gap="md">
            <Group justify="space-between" align="start" wrap="wrap">
                <div>
                    <Title order={2}>{q.data?.session.title || '(без заголовка)'}</Title>
                    {q.data && (
                        <Group gap="xs" mt={4} fz="sm" c="dimmed" wrap="wrap">
                            <Text fz="sm">{q.data.session.user}</Text>
                            <Text fz="sm">·</Text>
                            <Text fz="sm">{q.data.session.owner}/{q.data.session.repo}</Text>
                            <Text fz="sm">·</Text>
                            <Text fz="sm">{fmtDate(q.data.session.updatedAt)}</Text>
                        </Group>
                    )}
                </div>
                <Group gap="xs">
                    <Link to="/admin/chats"><Text fz="sm">← к списку</Text></Link>
                    <Button color="red" variant="subtle" size="xs" onClick={openConfirm}>
                        Удалить сессию
                    </Button>
                </Group>
            </Group>

            <Alert color="yellow" variant="light">
                Вы просматриваете чужую сессию как админ. Действие залогировано в audit.
            </Alert>

            {q.isLoading && <Center my="lg"><Loader /></Center>}
            {q.error && <Alert color="red">{(q.error as Error).message}</Alert>}
            {q.data && (
                <Stack gap="sm">
                    {q.data.messages.length === 0 && <Text c="dimmed">пусто</Text>}
                    {q.data.messages.map(m => (
                        <Paper
                            withBorder
                            p="sm"
                            key={m.id}
                            bg={m.role === 'user' ? 'var(--mantine-color-dark-6)' : 'var(--mantine-color-dark-7)'}
                        >
                            <Group gap="xs" mb={4}>
                                <Badge size="xs" variant="light" color={m.role === 'user' ? 'blue' : 'green'}>{m.role}</Badge>
                                <Text fz="xs" c="dimmed">{fmtDate(m.createdAt)}</Text>
                            </Group>
                            <Text style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }} fz="sm">
                                {m.content}
                            </Text>
                        </Paper>
                    ))}
                </Stack>
            )}

            <Modal opened={confirmOpened} onClose={closeConfirm} fullScreen={!isDesktop} title="Удалить сессию?" centered>
                <Stack>
                    <Text>Сессия и все её сообщения будут удалены без возможности восстановления.</Text>
                    <Group justify="end">
                        <Button variant="default" onClick={closeConfirm}>Отмена</Button>
                        <Button color="red" loading={del.isPending} onClick={() => del.mutate()}>Удалить</Button>
                    </Group>
                </Stack>
            </Modal>

            {del.error && <Alert color="red">{(del.error as Error).message}</Alert>}

            {!q.data && !q.isLoading && (
                <Card withBorder><Text>Сессия не найдена</Text></Card>
            )}
        </Stack>
    );
}
